import { Logger } from '../../utils/logger';
import { ServiceResult } from '../../models/common';

/**
 * Base class for Application layer services
 * Provides standardized patterns for error handling, logging, and response formatting
 */
export abstract class BaseApplicationService {
  /**
   * Standardized error handling and logging for service methods
   * @param serviceName Name of the service for logging
   * @param methodName Name of the method for logging
   * @param operation The actual async operation to perform
   * @param metadata Additional metadata for logging
   * @returns Standardized response format
   */
  protected static async executeOperation<T>(
    serviceName: string,
    methodName: string,
    operation: () => Promise<T>,
    metadata?: Record<string, unknown>
  ): Promise<ServiceResult<T>> {
    try {
      const data = await operation();

      // Only log errors, not successful operations to reduce console noise
      return { data, error: null, success: true };
    } catch (err: unknown) {
      const errorMessage = this.extractErrorMessage(err);

      // Reduce noise for common "not found" errors during concurrent operations
      const isNotFoundError =
        errorMessage.includes('not found') || errorMessage.includes('No rows found');

      if (isNotFoundError) {
        // Log as debug instead of error for expected "not found" cases
        Logger.debug(`${serviceName}.${methodName}`, {
          message: errorMessage,
          operation: `${methodName}_not_found`,
          ...metadata,
        });
      } else {
        Logger.serviceError(serviceName, methodName, errorMessage, {
          operation: `${methodName}_catch`,
          ...metadata,
        });
      }

      return {
        data: null as T,
        error: errorMessage,
        success: false,
      };
    }
  }

  /**
   * Standardized error handling for operations that return success status
   * @param serviceName Name of the service for logging
   * @param methodName Name of the method for logging
   * @param operation The actual async operation to perform
   * @param metadata Additional metadata for logging
   * @returns Standardized success response format
   */
  protected static async executeSuccessOperation(
    serviceName: string,
    methodName: string,
    operation: () => Promise<void>,
    metadata?: Record<string, unknown>
  ): Promise<ServiceResult<boolean>> {
    try {
      await operation();

      // Only log errors, not successful operations to reduce console noise
      return { data: true, error: null, success: true };
    } catch (err: unknown) {
      const errorMessage = this.extractErrorMessage(err);
      Logger.serviceError(serviceName, methodName, errorMessage, {
        operation: `${methodName}_catch`,
        ...metadata,
      });
      return { data: false, error: errorMessage, success: false };
    }
  }

  /**
   * Standardized error handling for operations that return arrays
   * @param serviceName Name of the service for logging
   * @param methodName Name of the method for logging
   * @param operation The actual async operation to perform
   * @param metadata Additional metadata for logging
   * @returns Standardized array response format
   */
  protected static async executeListOperation<T>(
    serviceName: string,
    methodName: string,
    operation: () => Promise<T[]>,
    metadata?: Record<string, unknown>
  ): Promise<ServiceResult<T[]>> {
    try {
      const data = await operation();

      // Only log errors, not successful operations to reduce console noise
      return { data, error: null, success: true };
    } catch (err: unknown) {
      const errorMessage = this.extractErrorMessage(err);
      Logger.serviceError(serviceName, methodName, errorMessage, {
        operation: `${methodName}_catch`,
        ...metadata,
      });
      return { data: [], error: errorMessage, success: false };
    }
  }

  /**
   * Standardized validation helper
   * @param serviceName Name of the service for logging
   * @param validations Array of validation objects
   * @returns First validation error or null if all pass
   */
  protected static validateInput(
    serviceName: string,
    validations: Array<{ condition: boolean; message: string }>
  ): string | null {
    for (const validation of validations) {
      if (!validation.condition) {
        Logger.warn(`${serviceName} validation failed`, {
          service: serviceName,
          operation: 'validation_failed',
          error: validation.message,
        });
        return validation.message;
      }
    }
    return null;
  }

  /**
   * Helper to safely extract error message from various error types
   * @param err The error object
   * @returns Standardized error message
   */
  protected static extractErrorMessage(err: unknown): string {
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    if (err && typeof err === 'object' && 'message' in err) {
      return String((err as { message: unknown }).message);
    }
    if (err && typeof err === 'object' && 'error' in err) {
      return String((err as { error: unknown }).error);
    }
    return 'An unexpected error occurred';
  }

  /**
   * Helper to turn anything thrown into an Error (for logging APIs that need one)
   * @param err The thrown value
   * @returns The same Error if it already is one, otherwise a new Error with the extracted message
   */
  protected static toError(err: unknown): Error {
    return err instanceof Error ? err : new Error(this.extractErrorMessage(err));
  }

  /**
   * Helper to format metadata for logging
   * @param metadata Raw metadata object
   * @returns Sanitized metadata object
   */
  protected static formatMetadata(metadata?: Record<string, unknown>): Record<string, unknown> {
    if (!metadata) return {};

    // Deep-copy so stripping secrets never mutates the caller's object.
    const sanitized = structuredClone(metadata);

    // Remove password fields at any depth
    const stripPasswords = (obj: Record<string, unknown>) => {
      for (const key of Object.keys(obj)) {
        if (key.toLowerCase() === 'password') {
          delete obj[key];
        } else if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
          stripPasswords(obj[key] as Record<string, unknown>);
        }
      }
    };
    stripPasswords(sanitized);

    // Truncate large strings
    Object.keys(sanitized).forEach(key => {
      if (typeof sanitized[key] === 'string' && (sanitized[key] as string).length > 200) {
        sanitized[key] = (sanitized[key] as string).substring(0, 200) + '...';
      }
    });

    return sanitized;
  }
}
